import type { FC } from "react";

import styled from "styled-components";

import { SWReactCountryIcons } from "@similarweb/icons";

import { countryTextByIdFilter } from "app/filters/ngFilters";

import type { ITableCellProps } from "../interfaces/ITableCellProps";

import { CountryFlagContainer } from "./CountryCellContent";
import { Text } from "@similarweb/ui-components/foundations";
import { colorsPalettes } from "@similarweb/styles";

const DEFAULT_MAX_COUNTRIES = 5;

const StyledCountriesList = styled.div<{ $isDisabled?: boolean }>`
    display: inline-flex;
    align-items: center;
    height: 100%;
    opacity: ${({ $isDisabled }) => $isDisabled && 0.5};
`;

// this will temporarily fix the issue with the country icon align center bug
const StyledCountryFlagContainer = styled(CountryFlagContainer)`
    > div {
        display: flex;
    }
`;

type MultiCountryProps = {
    countries: Array<number>;
    maxCountries?: number;
    className?: string;
    countryFlagContainerSize?: { width?: string; height?: string };
    id?: number;
    showCountryText?: boolean;
    isDisabled?: boolean;
    extraCountLink?: string;
};

export const MultiCountry: FC<MultiCountryProps> = ({
    countries,
    className,
    maxCountries = DEFAULT_MAX_COUNTRIES,
    countryFlagContainerSize,
    id,
    showCountryText,
    isDisabled,
    extraCountLink,
}) => {
    const extraCount = countries.length - maxCountries;

    return (
        <StyledCountriesList
            $isDisabled={isDisabled}
            className={className}
        >
            {countries.slice(0, maxCountries).map((countryCode, index) => (
                <StyledCountryFlagContainer
                    key={`${id}-${countryCode}-${index}`}
                    width={countryFlagContainerSize?.width}
                    height={countryFlagContainerSize?.height}
                >
                    <SWReactCountryIcons countryCode={countryCode} />
                </StyledCountryFlagContainer>
            ))}
            {countries.length === 1 && showCountryText ? countryTextByIdFilter()(countries[0]) : null}
            {countries.length > maxCountries ? (
                extraCountLink ? (
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.blue[400]}
                        as="a"
                        href={extraCountLink}
                    >
                        +{extraCount} domains
                    </Text>
                ) : (
                    <span>+{extraCount}</span>
                )
            ) : null}
        </StyledCountriesList>
    );
};

export const MultiCountryCell: FC<ITableCellProps<readonly number[]>> = ({ value: countries, className, rowIndex }) => {
    return (
        <MultiCountry
            id={rowIndex}
            countries={countries as number[]}
            className={className}
        />
    );
};
